import { Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/context/LanguageProvider";
import { MainScore as MainScoreType } from "src/api/client-api/olapScores/olapScoresSchema";
import { formatNumber } from "src/utils/formatting";
import { getLcaStageBreakdown, type LcaStage } from "src/pages/Product/v2/ProductFormV2/sectionScores";
import { ScoreProgressBar } from "src/components/ScoreState/ScoreProgressBar";

type LifeCycleStagesCardProps = {
    mainScore: MainScoreType | undefined;
};

/**
 * "Life Cycle Stages" card from Figma `12817:68930`. Each row pairs a
 * stage label with its share of the total score.
 *
 * Once OLAP exposes at least one lifecycle bucket, all canonical stages remain
 * visible. Missing and explicit-zero stages are presented as `0.00%` with the
 * minimum-width Figma bar segment. A fully unavailable breakdown stays hidden.
 *
 * End of Life is intentionally excluded per the product spec ("ne fais pas
 * apparaitre End of life") even though the source Figma frame includes it.
 */
export const LifeCycleStagesCard = (props: LifeCycleStagesCardProps) => {
    const { mainScore } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const theme = useTheme();

    const getStageLabel = (stage: LcaStage) => {
        switch (stage) {
            case "ingredients":
                return t("product.v2.score_live.lca.raw_materials", "Raw Materials");
            case "transformation":
                return t("product.v2.score_live.lca.processing", "Processing");
            case "packaging":
                return t("product.v2.score_live.lca.packaging", "Packaging");
            case "transport":
                return t("product.v2.score_live.lca.transport", "Transport");
            case "distribution":
                return t("product.v2.score_live.lca.distribution", "Distribution");
            case "consumption":
                return t("product.v2.score_live.lca.usage", "Usage");
        }
    };

    const breakdown = getLcaStageBreakdown(mainScore);
    const hasLifecycleBreakdown = breakdown.rows.some((row) => row.value !== null);

    if (!hasLifecycleBreakdown) {
        return null;
    }

    return (
        <Stack gap={theme.spacing(2)}>
            <Typography component="h5" variant="h5" color={theme.custom.grey[2800]}>
                {t("product.v2.score_live.lca.section_title", "Life Cycle Stages")}
            </Typography>
            <Stack gap={theme.spacing(1)}>
                {breakdown.rows.map((row) => {
                    const displayedPercentage = Math.min(100, Math.max(0, row.percentage ?? 0));
                    const formattedMetric = `${formatNumber(displayedPercentage, currentLanguage, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}%`;
                    const stageLabel = getStageLabel(row.key);

                    return (
                        <Stack key={row.key} gap={theme.spacing(0.5)}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={theme.spacing(3)}
                            >
                                <Typography variant="body-medium" color={theme.custom.grey[2800]}>
                                    {stageLabel}
                                </Typography>
                                <Typography
                                    variant="body-medium"
                                    sx={{
                                        color: theme.custom.grey[2800],
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {formattedMetric}
                                </Typography>
                            </Stack>
                            <ScoreProgressBar label={`${stageLabel} ${formattedMetric}`} value={displayedPercentage} />
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
};
